// Keep on this device: the preference (default on), keeping a copy of an opened song, and reopening it from the library.

import { askPersistentOnce, keepCopy, readCopy } from '../copies.js';
import type { TrackInfo } from '../engine/engine.js';
import { listSongs, markKept, type SongRecord } from './library.js';
import { announce, showToast } from './ui.js';

/** After a song has opened: store its copy, unless it is already kept. Says what happened in a sentence when it did not work. */
export async function keepSong(file: File, track: TrackInfo): Promise<void> {
  try {
    const before = (await listSongs()).find((s) => s.id === track.id);
    if (before?.kept) return;
  } catch { /* no list: try the copy anyway */ }
  const r = await keepCopy(track.id, file);
  if (r.kind === 'kept') {
    await markKept(track, true, r.bytes, file.type).catch(() => { /* the copy is there; the list will say "Needs the file" */ });
    announce('Kept on this device. Open it again from Your songs.'); // Home says so on the song; no toast over the count
    void askPersistentOnce();
  } else if (r.kind === 'full') {
    showToast('There is no room to keep a copy of this song. Remove a kept song on Home to make space. The song plays as usual.');
  } else {
    showToast('This browser cannot keep a copy of the song here, so you will choose the file again next time. The song plays as usual.');
  }
}

/** The kept copy of a song as a File, or null (and the library is corrected) when it is gone. */
export async function copyOf(song: SongRecord): Promise<File | null> {
  const file = await readCopy(song.id, song.name, song.type ?? '');
  if (!file) await markKept(song, false).catch(() => {});
  return file;
}
